'use client'

import { Button, Checkbox, Input, Textarea, useDevice } from '@lune-fe/lune-ui-lib'
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import { FC, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

import TwoColumnsWithBackgroundImage from '@/components/TwoColumnsWithBackgroundImage'
import { useNewsletter } from '@/hooks/useNewsLetter'

// eslint-disable-next-line complexity
const ContactUsForm: FC<{ hubspotFormId: string }> = ({ hubspotFormId }) => {
    const { smScreen, xsScreen, mdScreen } = useDevice()
    const { isValidEmail, toHubspot } = useNewsletter(
        hubspotFormId,
        [
            'Contact us',
            'Tell us how we can help and we’ll get in touch shortly.',
            'I am happy to receive marketing communications from Lune',
        ],
        { gtagEvent: 'form_submission' },
    )

    const [name, setName] = useState<string>('')
    const [lastName, setLastName] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [company, setCompany] = useState<string>('')
    const [nameError, setNameError] = useState<boolean>(false)
    const [lastNameError, setLastNameError] = useState<boolean>(false)
    const [emailError, setEmailError] = useState<boolean>(false)
    const [companyError, setCompanyError] = useState<boolean>(false)
    const [messageError, setMessageError] = useState<boolean>(false)
    const [marketingConsent, setMarketingConsent] = useState<boolean>(false)
    const [message, setMessage] = useState<string>('')
    const [success, setSuccess] = useState<boolean>()
    const [debouncedEmail] = useDebounce(email, 1000)

    useEffect(() => {
        if (isValidEmail(email) || email === '') {
            setEmailError(false)
        } else {
            setEmailError(true)
        }
    }, [debouncedEmail, email, isValidEmail])

    useEffect(() => {
        setNameError(false)
    }, [name])

    useEffect(() => {
        setLastNameError(false)
    }, [lastName])

    const onSubmit = async () => {
        const validName = name.length
        const validLastName = lastName.length
        const validEmail = isValidEmail(email)
        const validCompany = company.length
        const validMessage = message.length

        if (!validName || !validEmail || !validCompany || !validMessage) {
            !validName && setNameError(true)
            !validLastName && setLastNameError(true)
            !validEmail && setEmailError(true)
            !validCompany && setCompanyError(true)
            !validMessage && setMessageError(true)

            return
        }

        const data = await toHubspot({
            firstname: name,
            lastname: lastName,
            email,
            company,
            message,
            legalConsent: 'gatedContent',
        })
        if (data.status === 'error') {
            setSuccess(false)
        } else {
            setSuccess(true)
            setName('')
            setLastName('')
            setEmail('')
            setMessage('')
            setCompany('')
            setMarketingConsent(false)
        }
    }

    return (
        <TwoColumnsWithBackgroundImage
            backgroundImage={'/images/landing-contact-us-desktop.png'}
            mobileBackgroundImage={'/images/landing-contact-us-mobile.png'}
            rightSide={<Box sx={{ height: smScreen || xsScreen ? '350px' : 'auto' }} />}
            leftSide={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        gap: mdScreen ? 3 : 4,
                    }}
                >
                    {success ? (
                        <Box
                            sx={{
                                height: mdScreen ? '508px' : '572px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: mdScreen ? 4 : 9,
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}
                        >
                            <Text variant={mdScreen ? 'h5' : 'h4'}>
                                Thank you, someone from the team will get in touch!
                            </Text>
                        </Box>
                    ) : (
                        <>
                            <Text variant={mdScreen ? 'h5' : 'h4'}>Contact us</Text>
                            <Text variant={mdScreen ? 'body2' : 'body1'}>
                                Tell us how we can help and we’ll get in touch shortly.
                            </Text>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    flexDirection: 'column',
                                    gap: 1,
                                    width: '100%',
                                    '.MuiInputBase-root ': {
                                        width: '100%',
                                    },
                                }}
                            >
                                <Input
                                    placeholder={'Name'}
                                    error={nameError}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                <Input
                                    placeholder={'Last name'}
                                    error={lastNameError}
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                />
                                <Input
                                    placeholder={'Company name'}
                                    error={companyError}
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                />
                                <Input
                                    placeholder={'Email'}
                                    error={emailError}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                <Textarea
                                    placeholder="Message"
                                    rows={4}
                                    wide={true}
                                    onChange={(e) => setMessage(e.target.value)}
                                    error={messageError}
                                />
                                <Box pt={4}>
                                    <Checkbox
                                        sx={{ marginRight: '12px' }}
                                        checked={marketingConsent}
                                        onChange={(e) => setMarketingConsent(e.target.checked)}
                                    />

                                    <Text
                                        variant="body3"
                                        onClick={() => setMarketingConsent(!marketingConsent)}
                                        sx={{ '&:hover': { cursor: 'pointer' } }}
                                    >
                                        I am happy to receive marketing communications from Lune
                                    </Text>
                                </Box>
                            </Box>
                            <Button
                                onClick={onSubmit}
                                leftIcon={<ArrowCircleRightIcon />}
                                sx={{ width: { xs: '100%', sm: 'auto' } }}
                            >
                                Contact us
                            </Button>
                        </>
                    )}
                </Box>
            }
        />
    )
}

export default ContactUsForm
